import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getQueryParams } from "helpers/queryParams";
import { AllianceEnum, getCatalogBaseUrl } from "enums/alliance.enum";
import useSkus from "hooks/useSkus";
import { clearSelectedFilters, setSelectedFilters, updateSelectedFilters } from "store/actions/skus";
import { SelectedSkuFilters, SkuSortTypeEnum } from "models/inventory/helpers/skuFilters.helpers";

const FILTER_KEYS: (keyof SelectedSkuFilters)[] = [
  "brand",
  "model",
  "year",
  "cc",
  "minPrice",
  "maxPrice",
  "searchText",
  "sort",
];

const parseFiltersFromQueryParams = (search: string): Partial<SelectedSkuFilters> => {
  const queryParams = getQueryParams(search);
  const filters: Partial<SelectedSkuFilters> = {};

  if (queryParams.brand) filters.brand = queryParams.brand;
  if (queryParams.model) filters.model = queryParams.model;
  if (queryParams.year) filters.year = parseInt(queryParams.year, 10);
  if (queryParams.cc) filters.cc = parseInt(queryParams.cc, 10);
  if (queryParams.minPrice) filters.minPrice = parseInt(queryParams.minPrice, 10);
  if (queryParams.maxPrice) filters.maxPrice = parseInt(queryParams.maxPrice, 10);
  if (queryParams.searchText) filters.searchText = queryParams.searchText;
  if (queryParams.sort) filters.sort = queryParams.sort as SkuSortTypeEnum;

  return filters;
};

const buildQueryString = (filters: Partial<SelectedSkuFilters>): string => {
  const params = FILTER_KEYS
    .filter((key) => filters[key] !== undefined && filters[key] !== null)
    .map((key) => `${key}=${encodeURIComponent(filters[key] as string | number)}`);

  return params.length > 0 ? `?${params.join("&")}` : "";
};

export const useSkuCatalogFilters = (alliance?: AllianceEnum) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { selectedFilters } = useSkus();
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    const initialFilters = parseFiltersFromQueryParams(window.location.search);
    dispatch(setSelectedFilters(initialFilters));
    setInitialized(true);
  }, []);

  useEffect(() => {
    if (!initialized) return;

    const baseUrl = getCatalogBaseUrl(alliance);
    const queryString = buildQueryString(selectedFilters ?? {});
    navigate(`${baseUrl}${queryString}`, { replace: true });
  }, [selectedFilters, initialized]);

  const updateCatalogFilters = (changes: Partial<SelectedSkuFilters>) => {
    Object.entries(changes).forEach(([key, value]) => {
      dispatch(updateSelectedFilters(key as keyof SelectedSkuFilters, value));
    });
  };

  const clearAllFilters = () => {
    dispatch(clearSelectedFilters());
  };

  return [updateCatalogFilters, clearAllFilters, initialized] as const;
};
